import React, { useState, useCallback } from 'react';
import moment from 'moment';
import { useSelector } from 'react-redux';
import Drawer from '@material-ui/core/Drawer';
import Box from '@material-ui/core/Box';
import CloseIcon from '@material-ui/icons/Close';
import IconButton from '@material-ui/core/IconButton';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import { Query, useMutation } from 'react-apollo';
import humanDuration from 'humanize-duration';
import MaterialTable from 'material-table';
import Spin from 'components/common/Spin';
import Select from 'components/form/Select';
import Button from 'components/common/Button';
import {
  GET_PAYMENT_ITEMS_FOR_BONUS,
  CREATE_BONUS_ITEM,
  UPDATE_PAYMENT_ITEMS
} from 'graphql/payments';
import { getFullName } from 'utils';
import { calcAmount } from 'components/pages/time-tracker/components/LogTime';
import useSnack from 'hooks/useSnack';
import TextField from 'components/form/Input';

const useStyles = makeStyles(() => ({
  drawer: {
    width: 900,
    padding: 20,
  },
  close: {
    position: 'absolute',
    right: 10,
    top: 10,
  },
  table: {
    'width': '100%',
    '& th': {
      textAlign: 'left',
    },
  },
}));

const CATEGORIES = {
  ncf: 'non client-facing',
  cf: 'client-facing',
};

const BonusPayment = ({ coach, onClose, refetch }) => {
  const classes = useStyles();
  const [category, setCategory] = useState();
  const [comment, setComment] = useState('');
  const [selections, setSelections] = useState([]);
  const snack = useSnack();
  const [createBonus] = useMutation(CREATE_BONUS_ITEM);
  const [updateItems] = useMutation(UPDATE_PAYMENT_ITEMS);
  const user = useSelector((s) => s.user.user);

  const calcBonus = useCallback(
    (items) => {
      if (category) {
        const totalMinutes = items.reduce((memo, i) => {
          return memo + i.duration;
        }, 0);
        const amount = calcAmount({
          duration: totalMinutes,
          category: category.category,
        });
        return {
          totalMinutes,
          amount,
        };
      }
      return {
        totalMinutes: 0,
        amount: 0,
      };
    },
    [category],
  );

  const onPay = async (bonus) => {
    try {
      const payload = {
        user_id: coach.id,
        date: moment().format('YYYY-MM-DD'),
        payment_category_id: category.value,
        comments: comment,
        duration: bonus.totalMinutes,
        total: parseInt(Number(bonus.amount) * 100),
        status: 'approved',
        approved: true,
        approved_by: user.id,
      };
      const rep = await createBonus({
        variables: {
          data: payload,
        },
      });
      await updateItems({
        variables: {
          ids: selections.map(i => i.id),
          data: {
            bonus_line_id: rep.data.row.id
          }
        }
      });
      snack.success('Success');
      refetch();
      onClose();
    } catch (e) {
      snack.error(e.message);
    }
  };

  return (
    <Drawer open anchor="right" classes={{ paper: classes.drawer }}>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Typography variant="h5">Bonus Payment</Typography>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Box>
      <Box marginBottom={3}>
        <Typography variant="subtitle2">{getFullName(coach)}</Typography>
      </Box>
      <Query
        query={GET_PAYMENT_ITEMS_FOR_BONUS}
        fetchPolicy="network-only"
        variables={{
          userId: coach.id,
          categories: ['cf', 'ncf'],
        }}
      >
        {({ data, loading, error }) => {
          if (loading) return <Spin spinning />;

          if (error) return error.message;

          if (!data.rows.length)
            return <Typography>No hours found for bonus.</Typography>;

          const bonus = calcBonus(selections);

          const STUDENTS_LOOKUP = data.rows.reduce((memo, i) => {
            memo[i.student_id] = getFullName(i.student);
            return memo;
          }, {});

          const columns = [
            { title: 'Student', field: 'student_id', lookup: STUDENTS_LOOKUP },
            { title: 'Date', field: 'date', type: 'date', filtering: false },
            {
              title: 'Category',
              field: 'payment_category.label',
              filtering: false,
            },
            { title: 'Duration (min)', field: 'duration', filtering: false },
            {
              title: 'Type',
              field: 'payment_category.type',
              lookup: CATEGORIES,
              filtering: false,
            },
          ];

          return (
            <Box>
              <MaterialTable
                options={{
                  toolbar: false,
                  paging: false,
                  filtering: true,
                  selection: true,
                }}
                columns={columns}
                data={data.rows}
                onSelectionChange={setSelections}
              />
              <Box mt={3}>
                <Typography paragraph variant="h6">
                  Calculate Bonus
                </Typography>
                <Select
                  isMulti={false}
                  value={category}
                  onChange={setCategory}
                  label="Select Bonus Rate"
                  options={data.bonus_rates.map((i) => ({
                    label: i.payment_category.label,
                    value: i.category_id,
                    category: {
                      rate: i.rate,
                      rateType: i.rate_type,
                    },
                  }))}
                />
                {category && selections.length ? (
                  <Box>
                    <Typography variant="h5">Bonus: ${bonus.amount}</Typography>
                    <Typography paragraph>
                      Total duration{' '}
                      {humanDuration(bonus.totalMinutes * 60 * 1000)} at $
                      {(category.category.rate / 100).toFixed(2)} -{' '}
                      {category.category.rateType} rate.
                    </Typography>
                    <TextField
                      multiline
                      fullWidth
                      rows={1}
                      placeholder="Remarks..."
                      value={comment}
                      onChange={(e) => setComment(e.target.value)}
                    />
                    <Button
                      disabled={!comment || !bonus.amount}
                      variant="contained"
                      color="primary"
                      onClick={() => onPay(bonus)}
                    >
                      Pay
                    </Button>
                  </Box>
                ) : null}
              </Box>
            </Box>
          );
        }}
      </Query>
    </Drawer>
  );
};

export default BonusPayment;
